import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { CurrentActivity } from '@/types/events';

export function useCurrentActivities(userId: string | null) {
  const [activities, setActivities] = useState<CurrentActivity[]>([]);

  const fetch = useCallback(async () => {
    if (!userId) {
      setActivities([]);
      return;
    }
    try {
      const { data: volunteerEvents, error } = await supabase
        .from('volunteers')
        .select(
          `
          event_id,
          department,
          events (
            id,
            name,
            location,
            image,
            start_date,
            end_date,
            status,
            is_active,
            created_by
          )
        `
        )
        .eq('volunteer_id', userId)
        .eq('is_active', true);

      if (error) {
        setActivities([]);
        return;
      }

      type EventShape = {
        id: string;
        name: string;
        location: string;
        image: string | null;
        start_date: string | null;
        end_date: string | null;
        status: string;
        is_active: boolean;
        created_by: string | null;
      };

      const rawList = (volunteerEvents ?? [])
        .map((item: unknown) => {
          const row = item as { event_id?: string; department?: string | null; events?: EventShape | EventShape[] | null };
          const ev = Array.isArray(row.events) ? row.events[0] : row.events;
          if (!ev || !ev.is_active) return null;
          return { row, event: ev };
        })
        .filter((x): x is { row: { department?: string | null }; event: EventShape } => x != null);

      // Batch-fetch organization names for all unique org IDs
      const orgIds = [...new Set(rawList.map((x) => x.event.created_by).filter(Boolean))] as string[];
      const orgNameMap: Record<string, string> = {};
      if (orgIds.length > 0) {
        const { data: orgProfiles } = await supabase
          .from('profiles')
          .select('id, first_name')
          .in('id', orgIds);
        for (const p of orgProfiles ?? []) {
          const profile = p as { id: string; first_name: string | null };
          if (profile.id) orgNameMap[profile.id] = profile.first_name ?? 'Unknown Organization';
        }
      }

      const list = rawList.map(({ row, event }) => {
          const startDate = event.start_date ? new Date(event.start_date) : null;
          const endDate = event.end_date ? new Date(event.end_date) : null;
          const today = new Date();
          const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

          let daysLeft: number | null = null;
          let daysUntilStart: number | null = null;
          let isEventStarted = false;
          let progress = 0;

          if (startDate && endDate) {
            const startDateStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
            const endDateStart = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
            isEventStarted = todayStart >= startDateStart;
            if (isEventStarted) {
              const timeDiff = endDateStart.getTime() - todayStart.getTime();
              daysLeft = Math.floor(timeDiff / (1000 * 60 * 60 * 24)) + 1;
              const totalDays = Math.floor((endDateStart.getTime() - startDateStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
              const elapsedDays = Math.floor((todayStart.getTime() - startDateStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
              progress = totalDays > 0 ? Math.round((elapsedDays / totalDays) * 100) : 0;
            } else {
              const timeDiff = startDateStart.getTime() - todayStart.getTime();
              daysUntilStart = Math.floor(timeDiff / (1000 * 60 * 60 * 24)) + 1;
            }
          }

          return {
            id: event.id,
            name: event.name,
            location: event.location,
            image: event.image,
            daysLeft,
            daysUntilStart,
            isEventStarted,
            progress,
            is_active: event.is_active,
            volunteerDepartments: typeof row.department === 'string' ? row.department.split(',').map((d) => d.trim()).filter(Boolean) : [],
            start_date: event.start_date,
            end_date: event.end_date,
            organization_id: event.created_by ?? null,
            organization_name: event.created_by ? (orgNameMap[event.created_by] ?? null) : null,
          };
        });

      setActivities(list);
    } catch {
      setActivities([]);
    }
  }, [userId]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { activities, refetch: fetch };
}
